import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, TextField as MUITextField, useTheme } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Button from "components/basic/button";
import Typography from "components/basic/typography";

import { DateField } from "../../../../../calendar/setAvailability/pickers";
import { AvailabilityData } from "../../../../../calendar/setAvailability/types";
import { Booking } from "../../../../../calendar/booking/types";
import TimeSlotPicker from "../../../TimeSlotPicker";
import { horizonMaxDate } from "../../../../helpers/availabilitySlots";
import { Meeting } from "../../../../types";

interface ConfirmPanelProps {
  meeting: Meeting;
  availability: AvailabilityData | null | undefined;
  bookings: Booking[];
  isVideo: boolean;
  isPending: boolean;
  proposing: boolean;
  date: string;
  from: string;
  link: string;
  note: string;
  durationMinutes: number;
  linkValid: boolean;
  confirmDisabled: boolean;
  requestedWhen: string;
  onToggleProposing: () => void;
  onDateChange: (date: string) => void;
  onSlotSelect: (from: string, to: string) => void;
  onLinkChange: (link: string) => void;
  onNoteChange: (note: string) => void;
  onConfirm: () => void;
  onClose: () => void;
  onDecline: () => void;
}

const ConfirmPanel: React.FC<ConfirmPanelProps> = ({
  meeting,
  availability,
  bookings,
  isVideo,
  isPending,
  proposing,
  date,
  from,
  link,
  note,
  durationMinutes,
  linkValid,
  confirmDisabled,
  requestedWhen,
  onToggleProposing,
  onDateChange,
  onSlotSelect,
  onLinkChange,
  onNoteChange,
  onConfirm,
  onClose,
  onDecline,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {t(proposing ? "confirm.propose_title" : "confirm.confirm_title")}
          </Typography>
          <Button variant="text" color="primary" onClick={onToggleProposing}>
            {t(proposing ? "confirm.use_requested" : "confirm.propose")}
          </Button>
        </Stack>

        {!proposing ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              backgroundColor: theme.palette.action.hover,
              borderRadius: 1,
              p: 1,
              mb: 1.5,
            }}
          >
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2">{requestedWhen}</Typography>
          </Stack>
        ) : (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" style={{ fontWeight: 600 }}>
              {t("confirm.select_date")}
            </Typography>
            <Box sx={{ mt: 0.5, mb: 1.5 }}>
              <DateField
                value={date}
                onChange={onDateChange}
                minDate={moment().format("YYYY-MM-DD")}
              />
            </Box>
            <Typography variant="caption" style={{ fontWeight: 600 }}>
              {t("confirm.select_time")}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <TimeSlotPicker
                date={date}
                durationMinutes={durationMinutes}
                availability={availability}
                bookings={bookings}
                excludeBookingId={meeting.id}
                value={from}
                onSelect={(slot) => onSlotSelect(slot.from, slot.to)}
              />
              {horizonMaxDate(availability?.bookingHorizon ?? "unlimited") && (
                <Typography
                  variant="caption"
                  style={{
                    color: theme.palette.text.secondary,
                    display: "block",
                    marginTop: 4,
                  }}
                >
                  {t("confirm.open_only")}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Meeting link */}
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {t(isVideo ? "confirm.link" : "confirm.link_optional")}
        </Typography>
        <MUITextField
          fullWidth
          size="small"
          placeholder="https://meet.google.com/abc-defg-hij"
          value={link}
          onChange={(e) => onLinkChange(e.target.value)}
          error={isVideo && link.length > 0 && !linkValid}
          helperText={
            isVideo && link.length > 0 && !linkValid
              ? t("confirm.invalid_link")
              : undefined
          }
          sx={{ mt: 0.5, mb: 1.5 }}
        />

        {/* Note */}
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {t("confirm.note")}
        </Typography>
        <MUITextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder={t("confirm.note_placeholder")}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          sx={{ mt: 0.5, mb: 2 }}
        />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {isPending ? (
            <Button variant="outlined" color="error" onClick={onDecline}>
              {t("confirm.decline")}
            </Button>
          ) : (
            <Button variant="outlined" color="primary" onClick={onClose}>
              {t("confirm.cancel")}
            </Button>
          )}
          <Button
            variant="contained"
            color="warning"
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {t("confirm.submit")}
          </Button>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default ConfirmPanel;
